import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import Add from '../../../visuals/Icons/Add';
import Bin from '../../../visuals/Icons/Bin';
import Cart from '../../../visuals/Icons/Cart';
import Subtract from '../../../visuals/Icons/Subtract';
import { IButtonAddToCartProps } from '../ButtonAddToCart';

const HorizontalButtonAddToCart: React.FC<IButtonAddToCartProps> = ({
  handleAddToCart,
  handleRemoveFromCart,
  max,
  quantity,
  minimumProductQuantity = 1,
}) => {
  // isAriaLiveEnabled: enable Aria-Live only after first interaction with the cta to prevent
  // quantities being randomly announced when using the product filters
  const [isAriaLiveEnabled, setIsAriaLiveEnabled] = useState(false);
  const tAddToCartButton = useTranslations('Actions.addToCartButton');
  const isNotInCart = quantity <= 0;
  const isMaxReached =
    max <= quantity || (max < minimumProductQuantity && isNotInCart);
  const isToBeRemovedFromCart = quantity === minimumProductQuantity;

  return (
    <div
      className="tw-flex tw-max-w-[126px] tw-items-center tw-rounded-full tw-border tw-border-solid tw-border-yellow-400 tw-bg-white"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <button
        className={`tw-lf-button-conversion tw-lf-button-icon tw-lf-button-icon-md ${
          isNotInCart ? 'tw-sr-only' : ''
        }`}
        onClick={(e) => {
          setIsAriaLiveEnabled(true);
          handleRemoveFromCart(e);
        }}
        disabled={isNotInCart}
        aria-label={tAddToCartButton('delete')}
      >
        {isToBeRemovedFromCart ? (
          <Bin aria-hidden focusable={false} />
        ) : (
          <Subtract aria-hidden focusable={false} />
        )}
      </button>
      <span
        role="region"
        aria-live={isAriaLiveEnabled ? 'assertive' : 'off'}
        className={`tw-pointer-events-none tw-mb-[-22px] tw-mt-[-22px] tw-box-border tw-inline-block tw-w-11 tw-py-6 tw-text-center tw-font-body tw-leading-6 ${
          isNotInCart ? 'tw-sr-only' : ''
        }`}
      >
        {quantity}
      </span>
      <button
        className="tw-lf-button-conversion tw-lf-button-icon tw-lf-button-icon-md"
        onClick={(e) => {
          setIsAriaLiveEnabled(true);
          handleAddToCart(e);
        }}
        disabled={isMaxReached}
        aria-label={tAddToCartButton(
          isNotInCart ? 'addToCart' : isMaxReached ? 'maximum' : 'add',
        )}
      >
        {isNotInCart && <Cart aria-hidden focusable={false} />}
        {!isNotInCart && isMaxReached && (
          <span className="tw-font-body tw-text-xs">MAX</span>
        )}
        {!isNotInCart && !isMaxReached && <Add aria-hidden focusable={false} />}
      </button>
    </div>
  );
};

export default HorizontalButtonAddToCart;
